/**
 * KB Cloud Source Service
 *
 * API client for per-user cloud import sources (Google Drive via Composio,
 * SharePoint via MSAL). Backed by `orgain.kb.api.cloud_source_api`.
 */

import axios from 'axios';
import { getApiEndpoint } from '@/utils/ERPUtils';

/**
 * Cross-component signal fired after any change to the user's cloud
 * connectors (connect, disconnect, delete). The sidebar listens and
 * re-checks whether a connector is active so the manual "Upload Documents"
 * nav entry hides/shows immediately, without a page reload.
 */
export const CLOUD_SOURCES_CHANGED_EVENT = 'orgain:cloud-sources-changed';

export const notifyCloudSourcesChanged = (): void => {
  window.dispatchEvent(new Event(CLOUD_SOURCES_CHANGED_EVENT));
};

export type CloudProvider = 'Google Drive' | 'SharePoint';

export type CloudConnectionStatus =
  | 'Disconnected'
  | 'Connecting'
  | 'Connected'
  | 'Expired'
  | 'Awaiting Admin Approval'
  | 'Error';

export type CloudCurrentStatus =
  | 'Idle'
  | 'Queued'
  | 'Discovering'
  | 'Syncing'
  | 'Complete'
  | 'Error';

export type CloudSyncFileStatus =
  | 'Pending'
  | 'Synced'
  | 'Skipped'
  | 'Failed'
  | 'Stale';

// Canonical runtime state computed server-side (compute_display_state).
// The card renders this verbatim instead of deriving state client-side.
export type CloudDisplayState =
  | 'Idle'
  | 'Queued'
  | 'Discovering'
  | 'Syncing'
  | 'Processing'
  | 'Paused'
  | 'Stopping'
  | 'Error';

export interface CloudSourceListRow {
  name: string;
  source_name: string;
  provider: CloudProvider;
  enabled: number;
  current_status: CloudCurrentStatus;
  // Optional only for rolling-deploy safety (older backend); the card falls
  // back to a local derivation mirroring the backend rules.
  display_state?: CloudDisplayState;
  connection_status: CloudConnectionStatus;
  connection_error: string | null;
  connected_account_email: string | null;
  composio_user_id?: string | null;
  sharepoint_site_url?: string | null;
  sharepoint_site_name?: string | null;
  drive_folder_ids?: string | null;
  sharepoint_folder_paths?: string | null;
  confirm_whole_drive_scan?: number;
  file_extension_filter?: string | null;
  include_subfolders?: number;
  sync_mode?: string;
  batch_size?: number;
  max_file_size_mb?: number;
  last_discovery_at?: string | null;
  last_activity_at?: string | null;
  next_scheduled_sync_at?: string | null;
  total_discovered: number;
  pending_count: number;
  synced_count: number;
  processing_count: number;
  skipped_count: number;
  failed_count: number;
  stale_count: number;
  consecutive_auth_failures: number;
  last_error_category?: string | null;
  last_error?: string | null;
  owner: string;
  // Display name of the owner, resolved server-side — "Connected by X"
  // attribution when another admin views a source they don't own.
  owner_full_name?: string | null;
  creation: string;
  modified: string;
}

export interface CloudSourceDetail extends CloudSourceListRow {
  // The Composio connection / SharePoint site+drive IDs are Password fields
  // (server-only secrets) and are NOT sent over the wire; only a presence
  // flag is exposed.
  composio_connected_account_id_set?: boolean;
  drive_folder_names?: string | null;
  confirm_whole_drive_scan?: number;
  // SharePoint Azure App credentials are platform-wide (configured by the
  // admin in KB Cloud Sync Settings), so they are deliberately absent here.
  sharepoint_token_expires_at?: string | null;
  has_sharepoint_access_token?: boolean;
  has_sharepoint_refresh_token?: boolean;
  default_category?: string | null;
  default_document_type?: string | null;
  default_department?: string | null;
  default_access_level?: string | null;
  parent_kb_folder?: string | null;
  stale_file_policy?: string | null;
  auto_disable_after_auth_failures?: number;
  status_heartbeat_at?: string | null;
}

export interface CloudSyncProgress {
  current_status: CloudCurrentStatus;
  display_state?: CloudDisplayState;
  connection_status: CloudConnectionStatus;
  total_discovered: number;
  pending_count: number;
  synced_count: number;
  processing_count: number;
  skipped_count: number;
  failed_count: number;
  stale_count: number;
  last_activity_at: string | null;
  last_discovery_at: string | null;
  last_error: string | null;
  last_error_category: string | null;
  consecutive_auth_failures: number;
}

export interface CloudSyncRun {
  name: string;
  started_at: string | null;
  finished_at: string | null;
  triggered_by: string | null;
  run_type: string | null;
  files_discovered: number;
  files_new: number;
  files_imported: number;
  files_skipped: number;
  files_failed: number;
  summary: string | null;
}

export interface CloudFileSyncRow {
  name: string;
  filename: string;
  file_path: string | null;
  provider_web_url: string | null;
  sync_status: CloudSyncFileStatus;
  error_category: string | null;
  skip_reason: string | null;
  discovered_at: string | null;
  synced_at: string | null;
  last_attempt_at: string | null;
  file_size: number | null;
  mime_type: string | null;
  attempts: number;
  orgain_document: string | null;
  is_poison_file: number;
  last_error: string | null;
}

export interface CloudProviderAvailability {
  status: 'ok' | 'error';
  // Master switch - when false the whole cloud-sources feature is dark.
  // Older backend responses may omit this; treat undefined as true so
  // existing deployments keep working until the migration runs.
  cloud_sync_enabled?: boolean;
  drive: boolean;
  sharepoint: boolean;
  providers?: string[];
  message?: string | null;
  // Configured "Sync later" time of day (HH:MM, 24h). Surfaced so the
  // schedule button + confirmation dialog can show the real time instead
  // of saying "midnight" when the admin has moved it.
  daily_sync_time?: string;
}

export interface CleanupGuidance {
  step: string;
  label: string;
  detail: string;
  url?: string;
}

export interface SharePointSiteOption {
  id: string;
  name: string | null;
  display_name: string | null;
  web_url: string | null;
}

export interface FolderListItem {
  /** Human-readable folder name (last segment of the path). */
  name: string;
  /** Full path from the source's natural root (e.g. "Sales/2024"). */
  path: string;
  /** Whether the folder has children worth drilling into. */
  has_subfolders: boolean;
}

const AXIOS_CFG = {
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
};

function endpoint(key: string): string {
  const url = getApiEndpoint(key);
  if (!url) throw new Error(`${key} endpoint not configured`);
  return url;
}

async function post<T>(key: string, body: Record<string, unknown> = {}): Promise<T> {
  const res = await axios.post<{ message: T }>(endpoint(key), body, AXIOS_CFG);
  return res.data.message;
}

// Provider availability is admin-set and changes maybe once at install.
// The plan specifies a 60s frontend cache to avoid re-checking on every
// page navigation. In-flight de-dup is included so racing tabs share one
// network call.
const AVAILABILITY_CACHE_TTL_MS = 60_000;
let availabilityCachedAt = 0;
let availabilityCachedValue: CloudProviderAvailability | null = null;
let availabilityInflight: Promise<CloudProviderAvailability> | null = null;

export const cloudSourceService = {
  async getProviderAvailability(
    opts: { force?: boolean } = {},
  ): Promise<CloudProviderAvailability> {
    const now = Date.now();
    if (
      !opts.force &&
      availabilityCachedValue &&
      now - availabilityCachedAt < AVAILABILITY_CACHE_TTL_MS
    ) {
      return availabilityCachedValue;
    }
    if (availabilityInflight) {
      return availabilityInflight;
    }
    availabilityInflight = post<CloudProviderAvailability>('CLOUD_GET_PROVIDER_AVAILABILITY')
      .then((v) => {
        availabilityCachedValue = v;
        availabilityCachedAt = Date.now();
        return v;
      })
      .finally(() => {
        availabilityInflight = null;
      });
    return availabilityInflight;
  },

  async listMySources(): Promise<{ sources: CloudSourceListRow[] }> {
    return post('CLOUD_LIST_MY_SOURCES');
  },

  async getSource(source: string): Promise<{ status: string; source: CloudSourceDetail }> {
    return post('CLOUD_GET_SOURCE', { source });
  },

  async saveSource(payload: {
    source?: string;
    provider?: CloudProvider;
    source_name?: string;
    enabled?: number;
    drive_folder_ids?: string;
    sharepoint_site_url?: string;
    sharepoint_site_name?: string;
    sharepoint_folder_paths?: string;
    confirm_whole_drive_scan?: number;
    // SharePoint Azure App credentials are no longer accepted here - they
    // live on KB Cloud Sync Settings (configured by the platform admin).
    file_extension_filter?: string;
    include_subfolders?: number;
    max_file_size_mb?: number;
    sync_mode?: string;
    batch_size?: number;
    stale_file_policy?: string;
    auto_disable_after_auth_failures?: number;
    default_category?: string;
    default_document_type?: string;
    default_department?: string;
    default_access_level?: string;
    parent_kb_folder?: string;
  }): Promise<{
    status: string;
    source: string;
    action: string;
    changed: string[];
    // True when the save reset discovery scope (next sync = full walk).
    scope_changed?: boolean;
    // Queued files from the previous scope that were purged by this save.
    cleared_pending?: number;
  }> {
    return post('CLOUD_SAVE_SOURCE', payload as Record<string, unknown>);
  },

  /**
   * Dry-run count of queued (Pending) files a scope change would clear,
   * WITHOUT saving. `save_source` purges the out-of-scope backlog silently
   * the instant a folder scope narrows; the Configure dialog calls this first
   * to confirm with the user before that destructive purge. Pass the proposed
   * folder value for the source's provider (same shape as saveSource).
   */
  async previewScopePurge(payload: {
    source: string;
    drive_folder_ids?: string;
    sharepoint_folder_paths?: string;
  }): Promise<{ status: string; source: string; count: number }> {
    return post('CLOUD_PREVIEW_SCOPE_PURGE', payload as Record<string, unknown>);
  },

  async deleteSource(
    source: string,
    revokeRemote: boolean = true,
  ): Promise<{
    status: string;
    // Present only when status === 'error' (e.g. the source still has
    // documents in the KB, or a sync is in progress). `message` is a ready
    // to-display, user-facing explanation from the backend.
    code?: string;
    message?: string;
    document_count?: number;
    // Present on success.
    deleted?: string;
    provider?: CloudProvider;
    revoked_remote?: boolean;
    composio_connection_revoked?: boolean;
    cleanup_guidance?: CleanupGuidance[];
  }> {
    return post('CLOUD_DELETE_SOURCE', {
      source,
      revoke_remote: revokeRemote ? 1 : 0,
    });
  },

  /**
   * Remove a source still stuck in "Pending Authorization" because the user
   * closed the OAuth popup without finishing. No-op (returns status "kept")
   * once the source reached Connected or Error, so those cards survive.
   */
  async cancelPendingOAuth(
    source: string,
  ): Promise<{ status: string; connection_status?: string }> {
    return post('CLOUD_CANCEL_PENDING_OAUTH', { source });
  },

  async startOAuth(source: string): Promise<{ status: string; redirect_url: string }> {
    return post('CLOUD_START_OAUTH', { source });
  },

  async checkOAuthStatus(source: string): Promise<{
    status: string;
    connected_account_email?: string | null;
    error?: string | null;
  }> {
    return post('CLOUD_CHECK_OAUTH_STATUS', { source });
  },

  async disconnect(source: string): Promise<{ status: string }> {
    return post('CLOUD_DISCONNECT', { source });
  },

  /**
   * Re-establish a source's connection. When the existing connection still
   * probes healthy the backend reuses it and returns `reconnected: true` with
   * a null `redirect_url` - there is no OAuth popup to open in that case.
   * Pass `force` to re-authorize even when the current connection works.
   */
  async reconnect(
    source: string,
    force = false,
  ): Promise<{
    status: string;
    redirect_url: string | null;
    reconnected?: boolean;
    message?: string;
  }> {
    return post('CLOUD_RECONNECT', { source, force: force ? 1 : 0 });
  },

  async testConnection(source: string): Promise<{ status: string; message: string }> {
    return post('CLOUD_TEST_CONNECTION', { source });
  },

  async listSharePointSites(source: string): Promise<{
    status: 'ok' | 'error';
    message?: string;
    sites: SharePointSiteOption[];
    count?: number;
  }> {
    return post('CLOUD_LIST_SHAREPOINT_SITES', { source });
  },

  async listFolders(
    source: string,
    parentPath: string = '',
  ): Promise<{
    status: 'ok' | 'error';
    message?: string;
    /** Stable machine code for the error (e.g. 'scope_insufficient'). */
    reason?: string;
    /** True when the user should reconnect the source to recover. */
    reconnect?: boolean;
    folders: FolderListItem[];
  }> {
    return post('CLOUD_LIST_FOLDERS', { source, parent_path: parentPath });
  },

  async triggerSync(
    source: string,
    forceFull: boolean = false,
    allowMismatch: boolean = false,
    when: 'now' | 'later' = 'now',
  ): Promise<{
    status: string;
    code?: string;
    message?: string;
    job_id?: string | null;
    scheduled_at?: string;
    scheduled_sync_cancelled?: boolean;
    queued_behind_active?: boolean;
    emails?: { connected?: string; owner?: string };
  }> {
    return post('CLOUD_TRIGGER_SYNC', {
      source,
      force_full: forceFull ? 1 : 0,
      allow_mismatch: allowMismatch ? 1 : 0,
      when,
    });
  },

  async triggerDiscoveryOnly(
    source: string,
    allowMismatch: boolean = false,
  ): Promise<{
    status: string;
    code?: string;
    message?: string;
    job_id?: string | null;
  }> {
    return post('CLOUD_TRIGGER_DISCOVERY_ONLY', {
      source,
      allow_mismatch: allowMismatch ? 1 : 0,
    });
  },

  async getSyncProgress(source: string): Promise<CloudSyncProgress> {
    return post('CLOUD_GET_SYNC_PROGRESS', { source });
  },

  async retryFailed(
    source: string,
    allowMismatch: boolean = false,
  ): Promise<{
    status: string;
    code?: string;
    reset_count?: number;
    message?: string;
    job_id?: string | null;
  }> {
    return post('CLOUD_RETRY_FAILED', {
      source,
      allow_mismatch: allowMismatch ? 1 : 0,
    });
  },

  // Abort the running sync (non-destructive): in-flight files finish, the
  // unstarted remainder returns to Pending, and the queue stays parked until
  // the next user-triggered sync. See backend stop_sync for full semantics.
  async stopSync(source: string): Promise<{ status: string; message?: string }> {
    return post('CLOUD_STOP_SYNC', { source });
  },

  async listSyncHistory(
    source: string,
    limit: number = 20,
  ): Promise<{ status: string; runs: CloudSyncRun[] }> {
    return post('CLOUD_LIST_SYNC_HISTORY', { source, limit });
  },

  async listFiles(
    source: string,
    opts: { sync_status?: string; error_category?: string; limit?: number; offset?: number } = {},
  ): Promise<{ status: string; rows: CloudFileSyncRow[]; total: number }> {
    return post('CLOUD_LIST_FILES', { source, ...opts });
  },

  async recomputeAggregates(source: string): Promise<{
    status: string;
    total_discovered: number;
    pending_count: number;
    synced_count: number;
    skipped_count: number;
    failed_count: number;
    stale_count: number;
  }> {
    return post('CLOUD_RECOMPUTE_AGGREGATES', { source });
  },

  async estimateScope(source: string): Promise<{
    status: string;
    file_count?: number;
    total_bytes?: number;
    human_size?: string;
    estimated_ocr_pages?: number;
    estimated_vectors?: number;
    capped?: boolean;
    note?: string | null;
    message?: string;
  }> {
    return post('CLOUD_ESTIMATE_SCOPE', { source });
  },
};

/**
 * Open the provider OAuth URL in a popup window and resolve when the source
 * row's `connection_status` becomes Connected (or rejects on cancel/error).
 *
 * Polls `check_oauth_status` every 3 s. Also listens for the postMessage
 * the SharePoint callback page emits when it finishes - this lets us close
 * the polling loop immediately on success rather than waiting up to 3 s.
 */
export interface OAuthCompleteOptions {
  pollIntervalMs?: number;
  timeoutMs?: number;
  onStatus?: (status: string) => void;
}

export async function runOAuthPopup(
  source: string,
  redirectUrl: string,
  opts: OAuthCompleteOptions = {},
): Promise<{ status: string; connected_account_email?: string | null }> {
  const pollInterval = opts.pollIntervalMs ?? 3000;
  const timeoutMs = opts.timeoutMs ?? 5 * 60 * 1000;

  const popup = window.open(
    redirectUrl,
    'cloud-oauth',
    'width=620,height=720,menubar=no,toolbar=no,location=yes,status=no',
  );
  if (!popup) {
    throw new Error('Popup blocked. Please allow popups for this site and try again.');
  }

  const started = Date.now();
  let popupClosedSeen = false;
  // Tolerate transient network blips. A single failed checkOAuthStatus
  // shouldn't blow up the OAuth flow - the user might just have a flaky
  // connection. Bail only after this many consecutive failures.
  const MAX_CONSECUTIVE_POLL_FAILS = 3;
  let consecutiveFails = 0;

  return new Promise((resolve, reject) => {
    let settled = false;
    let pollTimer: number | null = null;

    const cleanup = () => {
      if (pollTimer !== null) {
        window.clearInterval(pollTimer);
        pollTimer = null;
      }
      window.removeEventListener('message', onMessage);
    };

    const finishOk = (status: string, email?: string | null) => {
      if (settled) return;
      settled = true;
      cleanup();
      try { popup.close(); } catch { /* swallow */ }
      resolve({ status, connected_account_email: email ?? null });
    };

    const finishErr = (err: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      try { popup.close(); } catch { /* swallow */ }
      reject(err);
    };

    const onMessage = (ev: MessageEvent) => {
      const data = ev?.data;
      if (!data || typeof data !== 'object') return;
      if (data.type !== 'orgain.sharepoint_oauth') return;
      if (data.status === 'success') {
        finishOk('Connected', data.email);
      } else if (data.admin_consent) {
        // Reactive admin-consent (Option B): not a failure - the tenant admin
        // must approve, then the user retries. Resolve with the special status
        // so the caller shows a "waiting for your admin" banner, not an error.
        finishOk('Awaiting Admin Approval', null);
      } else if (data.status === 'error') {
        finishErr(new Error(data.message || 'SharePoint OAuth failed'));
      }
    };
    window.addEventListener('message', onMessage);

    pollTimer = window.setInterval(async () => {
      try {
        if (popup.closed) {
          if (!popupClosedSeen) {
            popupClosedSeen = true;
          } else {
            // Give one extra tick for any final postMessage / DB write
            try {
              const res = await cloudSourceService.checkOAuthStatus(source);
              opts.onStatus?.(res.status);
              if (res.status === 'Connected') {
                finishOk(res.status, res.connected_account_email);
                return;
              }
              if (res.status === 'Awaiting Admin Approval') {
                finishOk(res.status, null);
                return;
              }
            } catch { /* swallow */ }
            finishErr(new Error('Connection cancelled'));
            return;
          }
        }
        const res = await cloudSourceService.checkOAuthStatus(source);
        consecutiveFails = 0;            // any success resets the counter
        opts.onStatus?.(res.status);
        if (res.status === 'Connected') {
          finishOk(res.status, res.connected_account_email);
          return;
        }
        if (res.status === 'Awaiting Admin Approval') {
          finishOk(res.status, null);
          return;
        }
        if (res.status === 'Error' || res.status === 'Failed') {
          finishErr(new Error(res.error || 'OAuth failed'));
          return;
        }
        if (Date.now() - started > timeoutMs) {
          finishErr(new Error('OAuth timed out'));
        }
      } catch (err: unknown) {
        consecutiveFails += 1;
        if (consecutiveFails >= MAX_CONSECUTIVE_POLL_FAILS) {
          const msg = err instanceof Error ? err.message : 'Network error during OAuth';
          finishErr(new Error(msg));
        }
        // else: swallow and retry on the next tick - transient blip.
      }
    }, pollInterval);
  });
}
